import { MessageCircle, Phone, Mail, Clock } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Reveal } from "@/components/Reveal";
import { EnquiryForm } from "@/components/EnquiryForm";
import { ChatWidget } from "@/components/ChatWidget";
import { CONTACT, waLink, WA_DEFAULT_MSG } from "@/lib/site";

const CHANNELS = [
  {
    label: "WhatsApp",
    desc: "Fastest response — send your routes, volumes and vehicle needs.",
    value: CONTACT.whatsappDisplay,
    icon: MessageCircle,
    href: waLink(WA_DEFAULT_MSG),
    external: true,
    testid: "contact-page-whatsapp",
    accent: "text-[#25D366]",
    tile: "bg-[#25D366]/10 border-[#25D366]/40",
  },
  {
    label: "Call Dispatch",
    desc: "Speak directly to the owner-operator about your contract.",
    value: CONTACT.callDisplay,
    icon: Phone,
    href: `tel:${CONTACT.callNumber}`,
    external: false,
    testid: "contact-page-call",
    accent: "text-[#FF6B00]",
    tile: "bg-[#FF6B00]/10 border-[#FF6B00]/40",
  },
  {
    label: "Email",
    desc: "Send RFQ documents, route sheets and scope documents.",
    value: CONTACT.email,
    icon: Mail,
    href: `mailto:${CONTACT.email}`,
    external: false,
    testid: "contact-page-email",
    accent: "text-white",
    tile: "bg-white/10 border-white/20",
  },
];

export default function Contact() {
  return (
    <main className="min-h-screen bg-[#080C14]">
      <Navbar />

      <section className="relative overflow-hidden px-5 pb-16 pt-32 md:pt-40 lg:px-8" data-testid="contact-hero">
        <div
          className="absolute inset-0 bg-[radial-gradient(60%_50%_at_50%_0%,rgba(255,107,0,0.12),transparent)]"
          aria-hidden="true"
        />
        <div className="relative mx-auto max-w-7xl">
          <Reveal>
            <p className="flex items-center gap-3 font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#FF6B00]">
              <span className="inline-block h-px w-8 bg-[#FF6B00]" aria-hidden="true" />
              Contact Dispatch
            </p>
          </Reveal>
          <Reveal delay={0.08}>
            <h1 className="mt-4 max-w-3xl font-display text-4xl font-black uppercase leading-[0.98] tracking-tight text-white sm:text-5xl lg:text-6xl">
              Start the contract conversation.
            </h1>
          </Reveal>
          <Reveal delay={0.16}>
            <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300 md:text-lg">
              Reach NGCONDE Courier Service on any channel below — or submit the
              partnership enquiry form and we&apos;ll come back with a dedicated
              proposal for your operation.
            </p>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 gap-4 md:grid-cols-3">
            {CHANNELS.map((c, i) => (
              <Reveal key={c.label} delay={0.1 + i * 0.08} className="h-full">
                <a
                  href={c.href}
                  target={c.external ? "_blank" : undefined}
                  rel={c.external ? "noopener noreferrer" : undefined}
                  data-testid={c.testid}
                  className="group flex h-full flex-col rounded-2xl border border-white/10 bg-[#0D1424] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#FF6B00]/50"
                >
                  <span
                    className={`flex h-12 w-12 items-center justify-center rounded-xl border ${c.tile} ${c.accent}`}
                  >
                    <c.icon className="h-6 w-6" />
                  </span>
                  <span className="mt-5 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-400">
                    {c.label}
                  </span>
                  <span className="mt-1 break-all font-display text-xl font-extrabold tracking-tight text-white">
                    {c.value}
                  </span>
                  <span className="mt-2 text-sm leading-relaxed text-slate-400">
                    {c.desc}
                  </span>
                </a>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div
              data-testid="contact-response-note"
              className="mt-6 flex items-center gap-3 rounded-xl border border-white/10 bg-[#0D1424] px-5 py-4"
            >
              <Clock className="h-4 w-4 shrink-0 text-[#FF6B00]" />
              <p className="text-sm text-slate-300">
                We respond to partnership enquiries within one business day.
                Contract clients get a dedicated dispatch line with direct
                owner access.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-5 pb-24 lg:px-8" data-testid="contact-form-section">
        <div className="mx-auto max-w-3xl">
          <Reveal>
            <div className="rounded-2xl border border-white/10 bg-[#0D1424] p-6 md:p-10">
              <h2 className="font-display text-2xl font-black uppercase tracking-tight text-white md:text-3xl">
                Partnership Enquiry
              </h2>
              <p className="mt-2 text-sm text-slate-400">
                Tell us about your routes, volumes and vehicle needs.
              </p>
              <div className="mt-8">
                <EnquiryForm />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      <ChatWidget />
      <Footer />
    </main>
  );
}
